/**
 * @file   : DropdownCard.js
 * @author : Cristian Tane (Cristian.Tane@AspiraConnect.com)
 * @date   : 4/28/2020, 4:13:15 PM
 */
import PropTypes from 'prop-types'
import React, { Component } from 'react'

import AButtonStandard from '../../../external/components/button/AButtonStandard'
import SimpleContainer from '../../../external/components/container/SimpleContainer'
import AInput from '../../../external/components/form/AInput'
import ACol from '../../../external/components/layout/ACol'
import AGrid from '../../../external/components/layout/AGrid'
import ARow from '../../../external/components/layout/ARow'
import Cont from '../../../external/components/layout/Cont'
import Section from '../../../external/components/layout/Section'
import ABaseLink from '../../../external/components/link/ABaseLink'
import AImg from '../../../external/components/media/img/AImg'
import TextBlock from '../../../external/components/text/TextBlock'
import * as FormHelperUtils from '../../../external/utilities/FormHelperUtils'
import { isEmpty, isUndefined } from '../../../external/utilities/GeneralUtils'
import { ROLES } from '../../../external/utilities/accessibility/Definitions'
import { sendToExternalPath } from '../../../router/RouteManager'
import HackMarketingContentWrapper from '../../sharedInternal/container/HackMarketingContentWrapper'
import { getTranslation, isData } from '../InfoUtil'
import SectionAd from './SectionAd'
import SectionDescription from './SectionDescription'
import SectionHeader from './SectionHeader'

const contentDropdown = {
  id: 'contentDropdown',
  type: FormHelperUtils.SELECT,
  isRequired: false,
}

class DropdownCard extends Component {
  constructor(props, context) {
    super(props, context)
    this.state = { selectedItem: DropdownCard.getFirstItem(props.section) }
  }

  static getSelectedItem(section, extId) {
    if (
      isEmpty(section) ||
      isUndefined(section.content) ||
      isUndefined(section.content.items)
    ) {
      return []
    }

    for (const item of section.content.items) {
      if (extId === item.id) {
        return item
      }
    }

    return []
  }

  componentDidUpdate(prevProps) {
    const { section } = this.props

    if (section !== prevProps.section && isEmpty(this.state.selectedItem)) {
      this.setState({
        selectedItem: DropdownCard.getFirstItem(section),
      })
    }
  }

  onInputChange(cmp, section, value) {
    this.setState({
      ...this.state,
      selectedItem: DropdownCard.getSelectedItem(section, value),
    })
  }

  static getAllItems(section) {
    if (
      isEmpty(section) ||
      isEmpty(section.content) ||
      isEmpty(section.content.items)
    ) {
      return []
    }

    return section.content.items
  }

  static getFirstItem(section) {
    if (
      isEmpty(section) ||
      isEmpty(section.content) ||
      isEmpty(section.content.items)
    ) {
      return []
    }

    return section.content.items[0]
  }

  static goToURL(link) {
    if (!isEmpty(link) && !isEmpty(link.link)) {
      sendToExternalPath(link.link, link.newWindow)
    }
  }

  getLinksUI() {
    const { selectedItem } = this.state
    const { intl } = this.props

    let linksUI = null

    if (!isEmpty(selectedItem.links)) {
      const linksList = selectedItem.links.map((link, i) => {
        let linkComponent = null
        if (link.isButton) {
          linkComponent = (
            <AButtonStandard
              key={i}
              formGroupPadding
              onClick={() => {
                DropdownCard.goToURL(link)
              }}
              cmpRef={link.token}
            >
              {getTranslation(intl, link.token, link.default)}
            </AButtonStandard>
          )
        } else {
          linkComponent = (
            <ABaseLink
              key={i}
              to={link.link}
              internal={link.linkInternal}
              newWin={link.newWindow}
              addRightMargin
              addLeftMargin
            >
              {getTranslation(intl, link.token, link.default)}
            </ABaseLink>
          )
        }

        return linkComponent
      })

      linksUI = <Cont>{linksList}</Cont>
    }

    return <Section className="t-center">{linksUI}</Section>
  }

  getClientsUI() {
    const { selectedItem } = this.state
    const { section, intl, infoData } = this.props
    const allItems = DropdownCard.getAllItems(section)
    const dropdownItems = allItems.map((item, i) => (
      <option key={i + 1} value={item.id}>
        {getTranslation(intl, item.token, item.default)}
      </option>
    ))
    const description = getTranslation(
      intl,
      selectedItem.excerpt.token,
      selectedItem.excerpt.default,
    )
    const imageURL = infoData.baseImagesFolder + selectedItem.imageURL
    const imageLabel = getTranslation(
      intl,
      selectedItem.imageDescription.token,
      selectedItem.imageDescription.default,
      infoData.infoToken,
    )

    let titleUI = null
    let clientUI = null
    let dropdownUI = null
    let imageUI = null

    titleUI = (
      <Cont>
        <SectionHeader intl={intl} section={section} infoData={infoData} />
        <SectionDescription intl={intl} section={section} infoData={infoData} />
      </Cont>
    )

    dropdownUI = (
      <Section>
        <SectionDescription
          intl={intl}
          section={section.content}
          infoData={infoData}
        />
        <AInput
          id={contentDropdown.id}
          type={contentDropdown.type}
          defaultValue={selectedItem.id}
          cmpRef={selectedItem.id}
          onChange={(ex) => {
            this.onInputChange(contentDropdown, section, ex.target.value)
          }}
        >
          {dropdownItems}
        </AInput>
      </Section>
    )

    if (!isEmpty(selectedItem)) {
      clientUI = (
        <Cont>
          <TextBlock isHTML isDefaultHTMLStyle content={description} />
          <Section />
          {this.getLinksUI()}
        </Cont>
      )

      if (!isEmpty(imageURL)) {
        imageUI = (
          <div className="dropdown-card__image hidden-xs">
            <AImg imageURL={imageURL} />
            <Cont
              asSpan
              cmpSRonly
              cmpRole={ROLES.DOC_STRUCTURE.IMG}
              cmpLabel={imageLabel}
            >
              {imageLabel}
            </Cont>
          </div>
        )
      }
    }

    return (
      <SimpleContainer>
        <AGrid fluid>
          <ARow bsPrefix={'dropdown-card__row row'}>
            <ACol xs={12} sm={6} md={6} lg={6}>
              {titleUI}
              <Section />
              {dropdownUI}
              {clientUI}
            </ACol>
            <ACol xs={12} sm={6} md={6} lg={6}>
              {imageUI}
              <Section />
            </ACol>
          </ARow>
        </AGrid>
      </SimpleContainer>
    )
  }

  render() {
    const { section, mainConfig } = this.props
    if (isData(section) && section.showSection) {
      return (
        <HackMarketingContentWrapper>
          {this.getClientsUI()}
          <SectionAd section={section} mainConfig={mainConfig} />
          <Section />
        </HackMarketingContentWrapper>
      )
    }

    return null
  }

  static propTypes = {
    intl: PropTypes.object.isRequired,
    section: PropTypes.object,
    mainConfig: PropTypes.object,
    infoData: PropTypes.object,
  }

  static defaultProps = {
    section: {},
    mainConfig: {},
    infoData: {},
  }
}

export default DropdownCard
